import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { EmptyRecordsState } from '../../common/ResponsiveRecordList';
import { getErrorMessage, unwrap } from './ExpensesShared';

const today = () => new Date().toISOString().slice(0, 10);
const monthsAgo = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

// Single read-only breakdown report (config/ledger.php whitelists only
// 'expenses/reports', one prefix, unlike receivables' three separate
// ap-ar-reports/* endpoints) - a best-effort match to this ledger's
// reporting convention (see receivables/ArReportsTab.js), read defensively
// since there's no caller elsewhere in this repo to verify the shape.
const ExpenseReportsView = () => {
  const [startDate, setStartDate] = useState(monthsAgo(3));
  const [endDate, setEndDate] = useState(today());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ledgerAPI.getExpenseReports({ start_date: startDate, end_date: endDate });
      setPayload(unwrap(res));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch expense report'));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = Array.isArray(payload)
    ? payload
    : (payload?.rows ?? payload?.categories ?? payload?.items ?? []);
  const rowAmount = (r) => Number(r.amount ?? r.total ?? r.total_amount ?? 0);
  const rowLabel = (r) => r.category_name ?? r.category?.category_name ?? r.label ?? r.name ?? '-';
  const total = payload?.total ?? rows.reduce((s, r) => s + rowAmount(r), 0);

  const handleExport = () => {
    const headers = ['Category', 'Amount'];
    const csvRows = rows.map((r) => [rowLabel(r), rowAmount(r)]);
    csvRows.push(['TOTAL', total]);
    downloadCsv(`expense-report-${startDate}-to-${endDate}.csv`, headers, csvRows);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField size="small" type="date" label="From" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button size="small" variant="outlined" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Generate'}</Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!rows.length}>Export CSV</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && rows.length === 0 ? (
        <EmptyRecordsState message="No expenses in this date range." />
      ) : (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell>{rowLabel(r)}</TableCell>
                  <TableCell align="right">{formatAccountingAmount(rowAmount(r))}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 700 }}>TOTAL</TableCell>
                <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700 }}>{formatAccountingAmount(total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ExpenseReportsView;
